import { Coding, QuestionnaireItemEnableWhen } from "@medplum/fhirtypes";

import {
  FormQuestionAnswer,
  JsonParsedValue,
  ParsedQuestionAnswer,
  RawQuestionAnswer,
} from "@/intake-forms/types/enableWhenWrapper";
import { EnableWhenBehavior } from "@/intake-forms/types/intake-forms";
import { getNestedFormAnswer } from "@/intake-forms/utils/intake-forms";

function isCodingLike(value: JsonParsedValue): value is Coding {
  return typeof value === "object" && value !== null;
}

function toQuestionAnswerValue(
  value: FormQuestionAnswer,
): RawQuestionAnswer | RawQuestionAnswer[] | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value : undefined;
  }

  return value;
}

function parseQuestionAnswer(
  answer: RawQuestionAnswer | null | undefined,
): ParsedQuestionAnswer | undefined {
  if (answer === null || answer === undefined) {
    return undefined;
  }

  if (typeof answer === "string") {
    try {
      const parsedAnswer = JSON.parse(answer) as JsonParsedValue;
      if (isCodingLike(parsedAnswer)) {
        return parsedAnswer;
      }
      return answer;
    } catch {
      return answer;
    }
  }

  if (typeof answer === "object") {
    return answer;
  }

  return String(answer);
}

function codingMatchesExpected(answer: Coding, expected: Coding): boolean {
  if (expected.code !== undefined && answer.code !== expected.code) {
    return false;
  }

  if (expected.system !== undefined && answer.system !== expected.system) {
    return false;
  }

  if (expected.display !== undefined && answer.display !== expected.display) {
    return false;
  }

  return (
    expected.code !== undefined || expected.system !== undefined || expected.display !== undefined
  );
}

function matchesEnableWhenAnswer(
  answer: ParsedQuestionAnswer,
  enableWhenRest: Omit<QuestionnaireItemEnableWhen, "operator" | "question">,
): boolean {
  if (enableWhenRest.answerCoding) {
    const expected = enableWhenRest.answerCoding;

    if (typeof answer === "object") {
      return codingMatchesExpected(answer, expected);
    }

    return answer === expected.code || answer === expected.display;
  }

  if (enableWhenRest.answerString !== undefined) {
    if (typeof answer === "object") {
      return (
        answer.code === enableWhenRest.answerString ||
        answer.display === enableWhenRest.answerString
      );
    }

    return answer === enableWhenRest.answerString;
  }

  return false;
}

function evaluateEnableWhenCondition(
  questionAnswer: RawQuestionAnswer | RawQuestionAnswer[],
  operator: QuestionnaireItemEnableWhen["operator"],
  enableWhenRest: Omit<QuestionnaireItemEnableWhen, "operator" | "question">,
): boolean {
  const compareAnswer = (answer: RawQuestionAnswer): boolean => {
    const parsedAnswer = parseQuestionAnswer(answer);
    if (parsedAnswer === undefined) {
      return false;
    }

    const matches = matchesEnableWhenAnswer(parsedAnswer, enableWhenRest);

    if (operator === "!=") {
      return !matches;
    }

    return matches;
  };

  if (Array.isArray(questionAnswer)) {
    if (operator === "!=") {
      return questionAnswer.every((answer) => compareAnswer(answer));
    }

    return questionAnswer.some((answer) => compareAnswer(answer));
  }

  switch (operator) {
    case "=":
    case "!=":
      return compareAnswer(questionAnswer);
    case ">":
      return parseInt(questionAnswer as string, 10) > enableWhenRest.answerInteger!;
    case "<":
      return parseInt(questionAnswer as string, 10) < enableWhenRest.answerInteger!;
    case ">=":
      return parseInt(questionAnswer as string, 10) >= enableWhenRest.answerInteger!;
    case "<=":
      return parseInt(questionAnswer as string, 10) <= enableWhenRest.answerInteger!;
    default:
      return false;
  }
}

function findAnswerDeep(obj: Record<string, unknown>, key: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  for (const k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) {
      const val = obj[k];
      if (val && typeof val === "object") {
        const found = findAnswerDeep(val as Record<string, unknown>, key);
        if (found !== undefined) return found;
      }
    }
  }
  return undefined;
}

export interface IsEnableWhenSatisfiedParams {
  enableWhen: QuestionnaireItemEnableWhen[] | undefined;
  formValues: Record<string, unknown>;
  parentPath?: string;
  enableWhenBehavior?: EnableWhenBehavior;
}

/** Same visibility rules as EnableWhenWrapper — true when the item should render. */
export function isEnableWhenSatisfied({
  enableWhen,
  formValues,
  parentPath,
  enableWhenBehavior,
}: IsEnableWhenSatisfiedParams): boolean {
  if (!enableWhen?.length) return true;

  const conditionsMet: boolean[] = [];

  for (const enableWhenItem of enableWhen) {
    const { operator, question, ...enableWhenRest } = enableWhenItem;

    const scopedKey = parentPath ? `${parentPath}.${question}` : question;
    let questionAnswer = getNestedFormAnswer(formValues, scopedKey);
    if (questionAnswer === undefined) {
      questionAnswer = getNestedFormAnswer(formValues, question);
    }
    if (questionAnswer === undefined) {
      questionAnswer = findAnswerDeep(formValues, question);
    }

    const normalizedQuestionAnswer = toQuestionAnswerValue(questionAnswer as FormQuestionAnswer);
    const conditionMet =
      normalizedQuestionAnswer !== undefined &&
      evaluateEnableWhenCondition(normalizedQuestionAnswer, operator, enableWhenRest);

    conditionsMet.push(conditionMet);
  }

  if (!enableWhenBehavior) return conditionsMet[0] ?? false;

  return enableWhenBehavior === EnableWhenBehavior.ALL
    ? conditionsMet.every((conditionMet) => conditionMet)
    : conditionsMet.some((conditionMet) => conditionMet);
}
